import { useContext } from 'react'
import { defaultLocale } from 'src/configurations'
import { ConfigContext } from 'src/context/ConfigContext/ConfigContextBase'
import { SupportedBrands } from 'src/enums'
import { getLocaleInfo } from 'src/localization/locales'
import { createTranslate, TranslateFn } from 'src/localization/translate'

/**
 * Returns the `translate()` function for the current locale and white-label
 * brand, read from ConfigContext (no Redux dependency).
 *
 * **Use this inside function components and custom hooks** — it is a hook, so
 * it must be called at the top level of a component or hook, never from a
 * plain function (enforced by the `local-rules/no-plain-call-of-hook-function`
 * lint rule).
 *
 * For code that runs outside a component render — model getters, validators,
 * redux action payloads, module-scope helpers — use `translateNode()` from
 * `src/localization/translateNode`, which returns an element that translates
 * itself when rendered.
 *
 * `translate()` is overloaded: all-string expressions return a `string`;
 * any React-element expression returns a `ReactElement`.
 */
export const useTranslate = (): TranslateFn => {
  const { locale, brand } = useContext(ConfigContext)
  const whitelabelBrand = brand.BRAND_NAME as SupportedBrands

  return createTranslate(
    getLocaleInfo(locale, whitelabelBrand).jsonTranslations,
    getLocaleInfo(defaultLocale, whitelabelBrand).jsonTranslations,
  )
}
